import {
  Clock, ExternalLink, Hash, MessageSquare, PhoneForwarded, PhoneOff, Send, Sparkles, User,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  askStatus, askedOf, clock, draftSources, lineStateText, secondsLeft, type AskTone,
} from "@/lib/closer-ask";
import {
  askIsOpen, useAskForCloser, useCloserAskAction, useCloserAskForCall, useCloserAskTargets,
  useDraftCloserNote, useHeldCloserAsk, useLeadCard, useMarkCloserAskTransferred, useSendToCloser,
  type HandoffMode,
} from "@/lib/queries";
import type { CloserAsk } from "@/lib/types";
import { cn } from "@/lib/utils";
import { rememberConsult, useAcdCall, useConsultOut } from "@/softphone/acd-call";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

/**
 * Ask for a closer (platform ADR-187). A setter on a live call asks a team on the campaign's closer
 * line, or one person on it; the ask is a card in that team's chat. The first Ready closer to
 * press Take call is held on My calls only, and the setter transfers the caller to them by name
 * with the Transfer the console already has. One card, drawn in the chat, under the setter's call
 * and over the closer's idle Phone page; core-api says what each reader may do with it.
 */

/** Ticks once a second while `on`, for the clocks on an open card. */
function useNow(on: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}

const HEAD: Record<AskTone, string> = {
  warning: "bg-warning-2 text-warning-11",
  success: "bg-success-2 text-success-11",
  neutral: "bg-gray-2 text-text-muted",
  accent: "bg-accent-2 text-accent-11",
};

function failure(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

/** The card of an ask: in a chat, under the setter's call, and over the closer's Phone page. */
export function CloserAskCard({
  ask, contact = null, compact = false,
}: {
  ask: CloserAsk;
  /** The contact as the chat shares it, drawn inside the card. */
  contact?: React.ReactNode;
  compact?: boolean;
}) {
  const live = askIsOpen(ask);
  const now = useNow(live);
  const s = askStatus(ask, now);
  const held = ask.state === "taken" && (ask.took || ask.mine);
  return (
    <div
      className={cn(
        "max-w-xl overflow-hidden rounded-xl border bg-surface",
        held ? "border-success-6 shadow-sm" : live ? "border-warning-6 shadow-sm" : "border-border",
      )}
    >
      <div className={cn("flex items-center gap-2 px-3 py-2 text-xs", HEAD[s.tone])}>
        {ask.state === "ended" ? (
          <PhoneOff className="size-3.5 shrink-0" aria-hidden />
        ) : ask.state === "expired" ? (
          <Clock className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <PhoneForwarded className="size-3.5 shrink-0" aria-hidden />
        )}
        <span className="shrink-0 font-semibold">{s.label}</span>
        <span className="min-w-0 flex-1 truncate opacity-90" title={s.line}>
          {s.line}
        </span>
        {ask.state === "waiting" && (
          <span className="flex shrink-0 items-center gap-1 font-mono tabular">
            <span className="size-1.5 animate-pulse rounded-full bg-danger-9" aria-hidden />
            LIVE
          </span>
        )}
      </div>
      {(ask.note || contact || (!compact && ask.lead_id)) && (
        <div className={cn("flex flex-col gap-2 px-3 py-2.5", !live && "opacity-70")}>
          {contact}
          {!contact && ask.lead_id && !compact && (
            <Link to={`/contacts/${ask.lead_id}`} className="flex items-center gap-1.5 text-xs text-accent-11 hover:underline">
              <ExternalLink className="size-3.5" aria-hidden />
              Open the contact
            </Link>
          )}
          {ask.note && (
            <p className="whitespace-pre-wrap break-words rounded-md bg-surface-sunken px-2.5 py-1.5 text-sm text-text">
              {ask.note}
            </p>
          )}
        </div>
      )}
      {live && <CloserAskActions ask={ask} now={now} />}
    </div>
  );
}

function CloserAskActions({ ask, now }: { ask: CloserAsk; now: number }) {
  const toast = useToast();
  const act = useCloserAskAction();
  const transfer = useTransferToTaker();
  const [error, setError] = React.useState<string | null>(null);
  const run = (action: "take" | "give-back" | "cancel") => {
    setError(null);
    act.mutate(
      { id: ask.id, action },
      {
        onSuccess: () => {
          if (action === "take") {
            toast.success("You took it", `${ask.asked_by_name ?? "The rep"} transfers the caller to you next.`);
          }
        },
        onError: (e) => setError(failure(e, "Could not do that just now.")),
      },
    );
  };
  const pending = (action: string) => act.isPending && act.variables?.action === action;
  const taker = ask.taken_by_name ?? "the closer";

  let row: React.ReactNode = null;
  if (ask.state === "waiting" && ask.mine) {
    row = (
      <>
        <span className="min-w-0 flex-1 text-xs text-text-muted">
          You stay on the call. The first closer to take it gets your transfer.
        </span>
        <Button size="sm" variant="outline" loading={pending("cancel")} onClick={() => run("cancel")}>
          Cancel
        </Button>
      </>
    );
  } else if (ask.state === "waiting" && ask.can_take) {
    row = (
      <Button size="sm" variant="default" loading={pending("take")} onClick={() => run("take")}>
        <PhoneForwarded />
        Take call
      </Button>
    );
  } else if (ask.state === "waiting" && ask.cannot_take && ask.cannot_take !== "It was not sent to you") {
    row = <span className="text-xs text-text-muted">{ask.cannot_take}</span>;
  } else if (ask.state === "taken" && ask.took) {
    row = (
      <>
        <span className="flex items-center gap-1.5 text-xs text-success-11">
          <Clock className="size-3.5" aria-hidden />
          Held for you {clock(secondsLeft(ask.hold_until, now))}
        </span>
        <span className="flex-1" />
        <Button size="sm" variant="outline" loading={pending("give-back")} onClick={() => run("give-back")}>
          Give it back
        </Button>
      </>
    );
  } else if (ask.state === "taken" && ask.mine) {
    const send = (mode: HandoffMode) => {
      setError(null);
      transfer.mutate(
        { ask, mode },
        {
          onSuccess: () =>
            toast.success(
              mode === "consult" ? `Calling ${taker}` : `Sent to ${taker}`,
              mode === "consult"
                ? "Your caller is on hold while you brief them."
                : `They wait on hold for ${taker}.`,
            ),
          onError: (e) => setError(failure(e, "Could not transfer the caller.")),
        },
      );
    };
    const sending = (mode: HandoffMode) => transfer.isPending && transfer.variables?.mode === mode;
    row = (
      <>
        <Button size="sm" variant="default" loading={sending("consult")} onClick={() => send("consult")}>
          <PhoneForwarded />
          Transfer to {taker}
        </Button>
        <Button size="sm" variant="outline" loading={sending("blind")} onClick={() => send("blind")}>
          Send straight over
        </Button>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" loading={pending("cancel")} onClick={() => run("cancel")}>
          Cancel
        </Button>
      </>
    );
  }
  if (row === null && error === null) return null;
  return (
    <div className="border-t border-border px-3 py-2">
      {row && <div className="flex flex-wrap items-center gap-2">{row}</div>}
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger-11">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The setter's transfer to the closer who took it: the handoff Transfer already makes, naming
 * them (ADR-031), with the setter's note; then the ask is marked transferred. "Transfer to" is a
 * consult, so the setter briefs the closer first; "Send straight over" is blind.
 */
function useTransferToTaker() {
  const sendToCloser = useSendToCloser();
  const marked = useMarkCloserAskTransferred();
  return useMutationLike(async ({ ask, mode }: { ask: CloserAsk; mode: HandoffMode }) => {
    const handoff = await sendToCloser.mutateAsync({
      callId: ask.caller_channel_id,
      note: ask.note ?? undefined,
      mode,
      toSipEndpointId: ask.taken_by_sip_endpoint_id,
    });
    if (mode === "consult") rememberConsult(handoff.id, ask.caller_channel_id);
    await marked.mutateAsync({ id: ask.id, handoffId: handoff.id });
  });
}

/** A small mutation over two others, with the shape the buttons read. */
function useMutationLike<T>(fn: (vars: T) => Promise<void>) {
  const [variables, setVariables] = React.useState<T | null>(null);
  const [isPending, setPending] = React.useState(false);
  const mutate = (vars: T, opts: { onSuccess?: () => void; onError?: (e: unknown) => void }) => {
    setVariables(vars);
    setPending(true);
    fn(vars)
      .then(() => opts.onSuccess?.())
      .catch((e: unknown) => opts.onError?.(e))
      .finally(() => setPending(false));
  };
  return { mutate, isPending, variables };
}

// --- the setter ----------------------------------------------------------------------------------

/** The ACD call the rep is talking on, when a closer could be asked for it. */
function useAskableCall(): string | null {
  const acd = useAcdCall();
  const call = acd?.state === "busy" ? acd.callerChannelId : null;
  const going = useConsultOut(call);
  return call && !going ? call : null;
}

/** Beside Transfer on a live ACD call: opens the dialog. Nothing while an ask is open (the strip
 *  under the call says where it is), or on a core-api that has no asks. */
export function AskCloserButton({ className }: { className?: string }) {
  const call = useAskableCall();
  const latest = useCloserAskForCall(call);
  const [open, setOpen] = React.useState(false);
  if (call === null || latest.isError || askIsOpen(latest.data)) return null;
  return (
    <>
      <Button type="button" variant="outline" className={cn("h-9", className)} onClick={() => setOpen(true)}>
        <MessageSquare />
        Ask for a closer
      </Button>
      <AskCloserDialog open={open} callId={call} onClose={() => setOpen(false)} />
    </>
  );
}

/** Under the setter's call while their ask is open, and once to say how it ended. */
export function AskCloserStrip() {
  const call = useAskableCall();
  const acd = useAcdCall();
  // While the setter briefs the closer the ask is over (transferred), and the consult bar shows.
  const latest = useCloserAskForCall(call ?? (acd?.state === "busy" ? acd.callerChannelId : null));
  const ask = latest.data ?? null;
  if (ask === null || !ask.mine) return null;
  // Open, or given up for want of a taker: the setter is still on the call and may ask again.
  return ask.state === "waiting" || ask.state === "taken" || ask.state === "expired" ? (
    <CloserAskCard ask={ask} compact />
  ) : null;
}

function AskCloserDialog({ open, callId, onClose }: { open: boolean; callId: string; onClose: () => void }) {
  const sees = seesCampaigns(useAuth().user?.role);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Ask for a closer</DialogTitle>
          <DialogDescription>
            {sees
              ? "The teams on this campaign's closer line. You stay on the call, and whoever takes it gets your transfer."
              : "The closer teams you can ask. You stay on the call, and whoever takes it gets your transfer."}
          </DialogDescription>
        </DialogHeader>
        {open && <AskCloserForm callId={callId} onDone={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

type Pick = { kind: "team"; id: number; name: string } | { kind: "person"; id: number; name: string };

function AskCloserForm({ callId, onDone }: { callId: string; onDone: () => void }) {
  const sees = seesCampaigns(useAuth().user?.role);
  const toast = useToast();
  const targets = useCloserAskTargets(callId);
  const acd = useAcdCall();
  const card = useLeadCard(acd?.leadId ?? null, acd?.sessionId ?? acd?.callerChannelId ?? null).data;
  const ask = useAskForCloser();
  const [pick, setPick] = React.useState<Pick | null>(null);
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const data = targets.data;

  // The note, drafted from the call so far (platform ADR-188): asked for once when the dialog
  // opens, and again when the setter asks. It never replaces what the setter has typed, except
  // when they ask for it again.
  const draft = useDraftCloserNote();
  const { mutate: requestDraft } = draft;
  const typed = React.useRef(false);
  const writeFromCall = React.useCallback(
    (replace: boolean) => {
      if (replace) typed.current = false;
      requestDraft(callId, {
        onSuccess: (made) => {
          if (!typed.current) setNote(made.note);
        },
      });
    },
    [callId, requestDraft],
  );
  const drafted = React.useRef(false);
  React.useEffect(() => {
    if (drafted.current) return;
    drafted.current = true;
    writeFromCall(false);
  }, [writeFromCall]);
  const sources = draft.data ? draftSources(draft.data.lines) : [];

  // The first team with somebody Ready, else the first team, picked to start with.
  React.useEffect(() => {
    if (pick !== null || !data || data.teams.length === 0) return;
    const team = data.teams.find((t) => t.ready > 0) ?? data.teams[0];
    if (team === undefined) return;
    setPick({ kind: "team", id: team.team_id, name: team.name });
  }, [data, pick]);

  const contactName = card ? [card.first_name, card.last_name].filter(Boolean).join(" ") || null : null;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pick === null) return;
    setError(null);
    ask.mutate(
      {
        callId,
        teamId: pick.kind === "team" ? pick.id : null,
        userId: pick.kind === "person" ? pick.id : null,
        note,
      },
      {
        onSuccess: (made) => {
          toast.success(`Asked ${askedOf(made)}`, "The first closer to take it gets your transfer.");
          onDone();
        },
        onError: (err) => setError(failure(err, "Could not ask for a closer.")),
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <DialogBody className="flex flex-col gap-4">
        {error && <Callout tone="danger">{error}</Callout>}
        {targets.isPending ? (
          <Spinner className="size-4" />
        ) : targets.isError ? (
          <Callout tone="danger">{failure(targets.error, "Could not read who to ask.")}</Callout>
        ) : data ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-text">Send to</p>
            {data.teams.length === 0 ? (
              <p className="text-xs text-text-muted">
                {sees
                  ? `No team is on ${data.queue_name} yet. An admin puts one there from the campaign's staffing. You can still ask one person below.`
                  : "No closer team is set up for this call yet. Tell your supervisor. You can still ask one person below."}
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Teams">
                {data.teams.map((t) => {
                  const on = pick?.kind === "team" && pick.id === t.team_id;
                  return (
                    <button
                      key={t.team_id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setPick({ kind: "team", id: t.team_id, name: t.name })}
                      className={cn(
                        "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left",
                        on ? "border-accent-8 bg-accent-2" : "border-border hover:bg-surface-hover",
                      )}
                    >
                      <Hash className="size-4 shrink-0 text-text-muted" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-text">{t.name}</span>
                        <span className={cn("block text-xs", t.ready > 0 ? "text-success-11" : "text-text-muted")}>
                          {t.closers === 0 ? "No closers in it" : `${t.ready} Ready of ${t.closers}`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {data.people.length > 0 && (
              <>
                <p className="mb-1.5 mt-3 text-xs text-text-muted">{sees ? `or one person on ${data.queue_name}` : "or one person"}</p>
                <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border" role="radiogroup" aria-label="People">
                  {data.people.map((p) => {
                    const on = pick?.kind === "person" && pick.id === p.user_id;
                    const state = lineStateText(p.state);
                    return (
                      <li key={p.user_id}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => setPick({ kind: "person", id: p.user_id, name: p.name ?? "them" })}
                          className={cn(
                            "flex w-full items-center gap-2.5 px-3 py-1.5 text-left",
                            on ? "bg-accent-2" : "hover:bg-surface-hover",
                            p.state !== "available" && !on && "opacity-70",
                          )}
                        >
                          <User className="size-4 shrink-0 text-text-muted" aria-hidden />
                          <span className="min-w-0 flex-1 truncate text-sm text-text">
                            {p.name ?? p.extension}
                            {!p.holds_closer && <span className="text-text-muted"> · no Closer skill</span>}
                          </span>
                          <span className={cn("shrink-0 text-xs", state.tone === "success" ? "text-success-11" : "text-text-muted")}>
                            {state.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        ) : null}
        {contactName && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-text">Goes with it</p>
            <p className="rounded-lg border border-border bg-surface-sunken px-3 py-2 text-sm text-text">
              {contactName}
              {card?.stage && <span className="text-text-muted"> · {card.stage}</span>}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label htmlFor="closer-ask-note" className="flex-1 text-xs font-medium text-text">
              Note for the closer
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={draft.isPending}
              onClick={() => writeFromCall(true)}
            >
              <Sparkles />
              Write it again from the call
            </Button>
          </div>
          <Textarea
            id="closer-ask-note"
            rows={7}
            maxLength={2000}
            value={note}
            onChange={(e) => {
              typed.current = true;
              setNote(e.target.value);
            }}
            placeholder={
              draft.isPending
                ? "Writing the note from the call…"
                : "What should they know before they pick up?"
            }
          />
          {draft.isPending ? (
            <p className="flex items-center gap-1.5 text-xs text-text-muted" aria-live="polite">
              <Spinner className="size-3" />
              Writing the note from what was said on the call…
            </p>
          ) : draft.data ? (
            <div className="flex flex-col gap-0.5 text-xs text-text-muted" aria-live="polite">
              {draft.data.notice && <p>{draft.data.notice}</p>}
              {sources.map((line) => (
                <p key={line}>{line}</p>
              ))}
              <p>Check it before you send it: the transcript can mishear a date or a name.</p>
            </div>
          ) : draft.isError ? (
            <p className="text-xs text-text-muted">{failure(draft.error, "Could not write the note from the call.")}</p>
          ) : null}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="default" disabled={pick === null} loading={ask.isPending}>
          <Send />
          {pick ? `Send to ${pick.name}` : "Send"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// --- the closer ----------------------------------------------------------------------------------

/** Over the closer's idle Phone page while they are held for a setter's transfer. */
export function CloserHoldBanner({ className }: { className?: string }) {
  const held = useHeldCloserAsk();
  const ask = held.data ?? null;
  if (ask === null || ask.state !== "taken") return null;
  return (
    <div className={className}>
      <CloserAskCard ask={ask} />
    </div>
  );
}
